import styled, { keyframes, css } from 'styled-components'

// Animations
const fadeIn = keyframes`
  from { opacity: 0; transform: translateY(-4px); }
  to { opacity: 1; transform: translateY(0); }
`

const spin = keyframes`
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
`

export const ModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: ${({ theme }) => theme.zIndex.modal};
  padding: ${({ theme }) => theme.spacing.medium};

  @media (max-width: ${({ theme }) => theme.breakpoints.tablet}) {
    padding: 0;
  }
`

export const ModalContent = styled.div`
  background: ${({ theme }) => theme.colors.background.primary};
  border-radius: ${({ theme }) => theme.borderRadius.large};
  box-shadow: ${({ theme }) => theme.shadows.xl};
  max-width: 1400px;
  width: 100%;
  max-height: 85vh;
  display: grid;
  grid-template-columns: 40% 60%;
  grid-template-rows: auto 1fr;
  overflow: hidden;

  @media (max-width: ${({ theme }) => theme.breakpoints.tablet}) {
    max-width: 100%;
    width: 100%;
    height: 100vh;
    height: 100dvh;
    max-height: 100vh;
    max-height: 100dvh;
    border-radius: 0;
    grid-template-columns: 1fr;
    grid-template-rows: auto 1fr;
  }
`

export const ModalHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: ${({ theme }) => theme.spacing.default}
    ${({ theme }) => theme.spacing.large};
  grid-column: 1 / -1;
  border-bottom: 1px solid ${({ theme }) => theme.colors.border.default};
  flex-shrink: 0;
  gap: ${({ theme }) => theme.spacing.small};
  min-width: 0;
  /* Guarantees the header is never squeezed out even if a parent's height
     math is wrong — the close button has to remain reachable. */
  min-height: ${({ theme }) => theme.touchTargets.comfortable};
  background: ${({ theme }) => theme.colors.background.primary};

  @media (max-width: ${({ theme }) => theme.breakpoints.tablet}) {
    padding: ${({ theme }) => theme.spacing.small}
      ${({ theme }) => theme.spacing.default};
    position: sticky;
    top: 0;
    z-index: 1;
  }
`

export const ModalTitle = styled.h3`
  font-size: ${({ theme }) => theme.typography.fontSize.section};
  font-weight: ${({ theme }) => theme.typography.fontWeight.semibold};
  color: ${({ theme }) => theme.colors.text.primary};
  margin: 0;
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.small};
  min-width: 0;
  flex: 1;

  @media (max-width: ${({ theme }) => theme.breakpoints.tablet}) {
    font-size: ${({ theme }) => theme.typography.fontSize.body};

    /* Keep the title on a single line with ellipsis on mobile */
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;

    span {
      overflow: hidden;
      text-overflow: ellipsis;
    }
  }
`

export const BackToChatButton = styled.button`
  display: none;

  @media (max-width: ${({ theme }) => theme.breakpoints.tablet}) {
    display: flex;
    align-items: center;
    justify-content: center;
    background: none;
    border: none;
    padding: ${({ theme }) => theme.spacing.xsmall};
    margin-right: ${({ theme }) => theme.spacing.xsmall};
    cursor: pointer;
    color: ${({ theme }) => theme.colors.text.secondary};
    border-radius: ${({ theme }) => theme.borderRadius.default};
    min-width: ${({ theme }) => theme.touchTargets.minimum};
    min-height: ${({ theme }) => theme.touchTargets.minimum};

    &:hover {
      color: ${({ theme }) => theme.colors.text.primary};
      background: ${({ theme }) => theme.colors.background.secondary};
    }
  }
`

export const CloseButton = styled.button`
  background: none;
  border: none;
  padding: ${({ theme }) => theme.spacing.xsmall};
  cursor: pointer;
  color: ${({ theme }) => theme.colors.text.secondary};
  transition:
    color ${({ theme }) => theme.transitions.fast},
    background ${({ theme }) => theme.transitions.fast};
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: ${({ theme }) => theme.borderRadius.default};
  flex-shrink: 0;

  &:hover {
    color: ${({ theme }) => theme.colors.text.primary};
    background: ${({ theme }) => theme.colors.background.secondary};
  }

  @media (max-width: ${({ theme }) => theme.breakpoints.tablet}) {
    min-width: ${({ theme }) => theme.touchTargets.minimum};
    min-height: ${({ theme }) => theme.touchTargets.minimum};
  }
`

// Chat Panel
export const ChatPanel = styled.div<{ $activeView?: 'chat' | 'pdf' }>`
  display: flex;
  flex-direction: column;
  height: 100%;
  min-height: 400px;
  max-height: 70vh;
  overflow: hidden;
  background: ${({ theme }) => theme.colors.background.secondary}10;
  min-width: 0;
  position: relative;

  @media (max-width: ${({ theme }) => theme.breakpoints.tablet}) {
    max-height: none;
    min-height: 0;
    display: ${({ $activeView }) => ($activeView === 'pdf' ? 'none' : 'flex')};
  }
`

export const MessagesContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing.default};
  flex: 1;
  overflow-y: auto;
  padding: ${({ theme }) => theme.spacing.default};

  /* Scrollbar styling */
  &::-webkit-scrollbar {
    width: 6px;
  }

  &::-webkit-scrollbar-track {
    background: transparent;
  }

  &::-webkit-scrollbar-thumb {
    background: ${({ theme }) => theme.colors.border.default};
    border-radius: ${({ theme }) => theme.borderRadius.small};

    &:hover {
      background: ${({ theme }) => theme.colors.text.tertiary};
    }
  }
`

// Empty State
export const EmptyState = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: ${({ theme }) => theme.spacing.xlarge};
  text-align: center;
  color: ${({ theme }) => theme.colors.text.tertiary};
  flex: 1;
`

export const EmptyStateIcon = styled.div`
  font-size: 38px;
  margin-bottom: ${({ theme }) => theme.spacing.default};
  opacity: 0.5;
`

export const EmptyStateText = styled.p`
  margin: 0;
  font-size: ${({ theme }) => theme.typography.fontSize.body};
  max-width: 280px;
`

// Message
export const MessageWrapper = styled.div<{ $role: 'user' | 'assistant' }>`
  display: flex;
  flex-direction: column;
  align-items: ${({ $role }) => ($role === 'user' ? 'flex-end' : 'flex-start')};
  animation: ${fadeIn} 0.2s ease-out;
`

export const MessageBubble = styled.div<{ $role: 'user' | 'assistant' }>`
  max-width: 90%;
  padding: ${({ theme }) => theme.spacing.small}
    ${({ theme }) => theme.spacing.default};
  border-radius: ${({ theme }) => theme.borderRadius.medium};
  font-size: ${({ theme }) => theme.typography.fontSize.small};
  line-height: 1.5;

  ${({ $role, theme }) =>
    $role === 'user'
      ? css`
          background: ${theme.colors.primary};
          color: ${theme.colors.text.inverse};
          border-bottom-right-radius: ${theme.borderRadius.small};
          white-space: pre-wrap;
        `
      : css`
          background: ${theme.colors.background.secondary};
          color: ${theme.colors.text.primary};
          border-bottom-left-radius: ${theme.borderRadius.small};
        `}

  /* Markdown styles for assistant messages */
  p {
    margin: 0 0 ${({ theme }) => theme.spacing.small};

    &:last-child {
      margin-bottom: 0;
    }
  }

  ul,
  ol {
    margin: ${({ theme }) => theme.spacing.small} 0;
    padding-left: ${({ theme }) => theme.spacing.large};
  }

  li {
    margin-bottom: ${({ theme }) => theme.spacing.micro};
  }

  strong {
    font-weight: ${({ theme }) => theme.typography.fontWeight.semibold};
  }

  code {
    padding: 2px 6px;
    border-radius: ${({ theme }) => theme.borderRadius.small};
    background: ${({ $role, theme }) =>
      $role === 'user'
        ? 'rgba(255, 255, 255, 0.2)'
        : theme.colors.background.tertiary};
    font-family: ${({ theme }) => theme.typography.fontFamily.mono};
    font-size: ${({ theme }) => theme.typography.fontSize.label};
  }

  pre {
    padding: ${({ theme }) => theme.spacing.small};
    border-radius: ${({ theme }) => theme.borderRadius.default};
    background: ${({ $role, theme }) =>
      $role === 'user'
        ? 'rgba(255, 255, 255, 0.2)'
        : theme.colors.background.tertiary};
    overflow-x: auto;

    code {
      padding: 0;
      background: none;
    }
  }
`

// Citations Container
export const CitationsContainer = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: ${({ theme }) => theme.spacing.xsmall};
  margin-top: ${({ theme }) => theme.spacing.small};
`

// Citation Chip
export const CitationChip = styled.button<{
  $granularity: 'land_use_plan' | 'land_use_plan_overlay' | 'region'
  $isActive?: boolean
}>`
  display: inline-flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.xsmall};
  padding: ${({ theme }) => theme.spacing.micro}
    ${({ theme }) => theme.spacing.small};
  font-size: ${({ theme }) => theme.typography.fontSize.label};
  border: none;
  border-radius: ${({ theme }) => theme.borderRadius.default};
  cursor: pointer;
  transition: all ${({ theme }) => theme.transitions.fast};

  ${({ $granularity, $isActive, theme }) => {
    if ($granularity === 'region') {
      return css`
        background: ${$isActive
          ? theme.colors.primary
          : theme.colors.primaryLight};
        color: ${$isActive ? theme.colors.text.inverse : theme.colors.primary};
        &:hover {
          background: ${theme.colors.primary};
          color: ${theme.colors.text.inverse};
        }
      `
    }
    return css`
      background: ${$isActive
        ? theme.colors.success
        : theme.colors.successLight};
      color: ${$isActive ? theme.colors.text.inverse : theme.colors.success};
      &:hover {
        background: ${theme.colors.success};
        color: ${theme.colors.text.inverse};
      }
    `
  }}
`

export const CitationIcon = styled.span`
  display: flex;
  align-items: center;
`

// Input Area
export const InputArea = styled.form`
  display: flex;
  gap: ${({ theme }) => theme.spacing.small};
  align-items: flex-end;
  padding: ${({ theme }) => theme.spacing.default};
  border-top: 1px solid ${({ theme }) => theme.colors.border.light};
  background: ${({ theme }) => theme.colors.background.primary};
  flex-shrink: 0;
`

export const TextInput = styled.textarea`
  flex: 1;
  min-height: 32px;
  max-height: 120px;
  padding: ${({ theme }) => theme.spacing.small};
  font-family: ${({ theme }) => theme.typography.fontFamily.sans};
  font-size: ${({ theme }) => theme.typography.fontSize.body};
  border: 1px solid ${({ theme }) => theme.colors.border.default};
  border-radius: ${({ theme }) => theme.borderRadius.default};
  resize: none;
  transition: border-color ${({ theme }) => theme.transitions.fast};

  &:focus {
    outline: none;
    border-color: ${({ theme }) => theme.colors.border.focus};
  }

  &::placeholder {
    color: ${({ theme }) => theme.colors.text.tertiary};
  }
`

export const MicButton = styled.button<{ $recording?: boolean }>`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  padding: 0;
  border: none;
  border-radius: ${({ theme }) => theme.borderRadius.default};
  background: ${({ theme, $recording }) =>
    $recording ? theme.colors.danger : theme.colors.background.tertiary};
  color: ${({ theme, $recording }) =>
    $recording ? theme.colors.text.inverse : theme.colors.text.secondary};
  cursor: pointer;
  transition: all ${({ theme }) => theme.transitions.fast};
  flex-shrink: 0;

  &:hover {
    background: ${({ theme, $recording }) =>
      $recording ? theme.colors.danger : theme.colors.background.secondary};
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`

export const SendButton = styled.button<{ $disabled?: boolean }>`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  padding: 0;
  border: none;
  border-radius: ${({ theme }) => theme.borderRadius.default};
  background: ${({ theme, $disabled }) =>
    $disabled ? theme.colors.background.tertiary : theme.colors.primary};
  color: ${({ theme, $disabled }) =>
    $disabled ? theme.colors.text.tertiary : theme.colors.text.inverse};
  cursor: ${({ $disabled }) => ($disabled ? 'not-allowed' : 'pointer')};
  transition: all ${({ theme }) => theme.transitions.fast};
  flex-shrink: 0;

  &:hover:not([disabled]) {
    background: ${({ theme }) => theme.colors.primaryHover};
  }

  @media (max-width: ${({ theme }) => theme.breakpoints.tablet}) {
    width: ${({ theme }) => theme.touchTargets.minimum};
    height: ${({ theme }) => theme.touchTargets.minimum};
  }
`

/**
 * Floating "View source" button shown on mobile in chat view when a PDF is
 * active — lets the user jump back into the PDF panel without re-tapping a
 * citation. Hidden on desktop (both panels are visible simultaneously there).
 */
export const ViewSourceFab = styled.button`
  display: none;

  @media (max-width: ${({ theme }) => theme.breakpoints.tablet}) {
    display: inline-flex;
    align-items: center;
    gap: ${({ theme }) => theme.spacing.xsmall};
    position: absolute;
    bottom: calc(
      ${({ theme }) => theme.touchTargets.minimum} +
        ${({ theme }) => theme.spacing.large}
    );
    right: ${({ theme }) => theme.spacing.default};
    padding: ${({ theme }) => theme.spacing.small}
      ${({ theme }) => theme.spacing.default};
    border: none;
    border-radius: ${({ theme }) => theme.borderRadius.round};
    background: ${({ theme }) => theme.colors.primary};
    color: ${({ theme }) => theme.colors.text.inverse};
    box-shadow: ${({ theme }) => theme.shadows.lg};
    font-size: ${({ theme }) => theme.typography.fontSize.small};
    font-weight: ${({ theme }) => theme.typography.fontWeight.medium};
    cursor: pointer;
    z-index: ${({ theme }) => theme.zIndex.elevated};
  }
`

// Loading Indicator
export const LoadingIndicator = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.small};
  padding: ${({ theme }) => theme.spacing.small};
  color: ${({ theme }) => theme.colors.text.tertiary};
  font-size: ${({ theme }) => theme.typography.fontSize.small};
`

export const LoadingSpinner = styled.div`
  width: 13px;
  height: 13px;
  border: 2px solid ${({ theme }) => theme.colors.border.light};
  border-top-color: ${({ theme }) => theme.colors.primary};
  border-radius: 50%;
  animation: ${spin} 0.8s linear infinite;
`

// Error Message
export const ErrorMessage = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.small};
  padding: ${({ theme }) => theme.spacing.small}
    ${({ theme }) => theme.spacing.default};
  margin: 0 ${({ theme }) => theme.spacing.default};
  background: #fef2f2;
  color: ${({ theme }) => theme.colors.danger};
  border-radius: ${({ theme }) => theme.borderRadius.default};
  font-size: ${({ theme }) => theme.typography.fontSize.small};
  cursor: pointer;
`

// PDF Viewer Panel
export const PDFViewerPanel = styled.div<{ $activeView?: 'chat' | 'pdf' }>`
  height: 100%;
  min-height: 500px;
  background: ${({ theme }) => theme.colors.background.primary};
  border-left: 1px solid ${({ theme }) => theme.colors.border.default};
  overflow: hidden;

  @media (max-width: ${({ theme }) => theme.breakpoints.tablet}) {
    border-left: none;
    min-height: 0;
    display: ${({ $activeView }) => ($activeView === 'pdf' ? 'block' : 'none')};
  }
`

// PDF Empty State
export const PDFEmptyState = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 100%;
  padding: ${({ theme }) => theme.spacing.xlarge};
  text-align: center;
  color: ${({ theme }) => theme.colors.text.tertiary};
  background: ${({ theme }) => theme.colors.background.secondary}20;
`

export const PDFEmptyStateIcon = styled.div`
  font-size: 64px;
  margin-bottom: ${({ theme }) => theme.spacing.default};
  opacity: 0.3;
`

export const PDFEmptyStateText = styled.p`
  margin: 0;
  font-size: ${({ theme }) => theme.typography.fontSize.body};
`
